"use client";

import React, { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type PortfolioData = {
  siteConfig: {
    id: string;
    title: string;
    description: string;
    applicationName: string;
    canonicalUrl: string;
    keywords: string[];
    authorName: string;
    authorUrl: string;
    ogImageUrl: string;
    twitterHandle: string;
    googleAnalyticsId?: string;
    googleSiteVerification?: string;
    logoUrl: string;
    footerBio: string;
    footerBottomTag: string;
    copyrightText: string;
    whatsappNumber: string;
    whatsappMessage: string;
    whatsappEnabled: boolean;
  };
  hero: {
    id: string;
    badgeText: string;
    greeting: string;
    fullName: string;
    designationPrefix: string;
    designation: string;
    bio: string;
    profileImageUrl: string;
    resumeUrl: string;
    resumeFilename: string;
    githubUrl: string;
    linkedinUrl: string;
    email: string;
  };
  about: {
    id: string;
    badge: string;
    heading: string;
    bioParagraphs: string[];
  };
  stats: Array<{
    id: string;
    label: string;
    value: string;
    order: number;
  }>;
  education: Array<{
    id: string;
    degree: string;
    institute: string;
    period: string;
    location: string;
    order: number;
  }>;
  certificates: Array<{
    id: string;
    title: string;
    provider: string;
    year: string;
    pdfUrl: string;
    downloadName: string;
    order: number;
  }>;
  skillCategories: Array<{
    id: string;
    name: string;
    accentColor: string;
    iconName: string;
    order: number;
    skills: Array<{
      id: string;
      name: string;
      iconUrl: string;
      order: number;
      categoryId: string;
    }>;
  }>;
  experiences: Array<{
    id: string;
    company: string;
    location: string;
    position: string;
    employmentType: string;
    workMode: string;
    period: string;
    isCurrent: boolean;
    responsibilities: string[];
    order: number;
    logoUrl?: string | null;
  }>;
  projects: Array<{
    id: string;
    title: string;
    description: string;
    imageUrl: string;
    category: string;
    tags: string[];
    liveUrl: string;
    githubUrl: string;
    highlights: string[];
    featured: boolean;
    order: number;
  }>;
  contactInfo: {
    id: string;
    email: string;
    phone: string;
    location: string;
    subLocation: string;
  };
};

type PortfolioContextType = {
  data: PortfolioData | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const PortfolioContext = createContext<PortfolioContextType>({
  data: null,
  isLoading: true,
  error: null,
  refresh: async () => {},
});

export function PortfolioProvider({
  children,
  initialData,
}: {
  children: ReactNode;
  initialData?: PortfolioData | null;
}) {
  const [data, setData] = useState<PortfolioData | null>(initialData || null);
  const [isLoading, setIsLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);

  const fetchPortfolio = async () => {
    try {
      const res = await fetch("/api/portfolio", { cache: "no-store" });
      if (!res.ok) throw new Error("Failed to load portfolio content from database.");
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (err: any) {
      console.error("Failed to fetch portfolio data:", err);
      setError(err.message || "Failed to load database content.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!initialData) {
      fetchPortfolio();
    }
  }, [initialData]);

  return (
    <PortfolioContext.Provider
      value={{
        data,
        isLoading,
        error,
        refresh: fetchPortfolio,
      }}
    >
      {children}
    </PortfolioContext.Provider>
  );
}

export function usePortfolio() {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error("usePortfolio must be used within a PortfolioProvider");
  }
  return context;
}
